import { observer } from 'mobx-react-lite';
import {
    Droppable,
    DragData,
    DropViewState,
    createDropViewState,
    DraggableItemTypes,
} from 'owa-dnd';
import * as React from 'react';
import type CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import type { CalendarPickerItemDragData } from '../store/schema/CalendarPickerItemDragData';
import { updateCalendarDisplayOrder, updateCalendarGroupDisplayOrder } from 'owa-calendar-update';
import getDraggedItemData from '../selectors/getDraggedItemData';

const styles = require('./CalendarPicker.scss');
const classNames = require('classnames/bind').bind(styles);

interface CalendarPickerDroppablePlaceholderProps {
    itemType: string;
    previousItemId: string;
    parentGroupId?: string;
    userIdentity: string;
    scenarioId: CalendarPickerScenarioId;
}

export default observer(function CalendarPickerDroppablePlaceholder(
    props: CalendarPickerDroppablePlaceholderProps
) {
    const [dropViewState, setDropViewState] = React.useState<DropViewState>(createDropViewState());

    // All drag functions and the variables inside them get memoized at the very beginning
    // of the drag. So once the drag starts, if the previousItemId prop changes, the values
    // of the memoized variable does not actually update when the onDrag or onDrop functions
    // get called. Therefore, a ref is needed to act as an instance variable so you have
    // the most up to date value.
    const prevItemId = React.useRef<string>();

    React.useEffect(() => {
        prevItemId.current = props.previousItemId;
    }, [props.previousItemId]);

    const canDrop = () => {
        const dragData = getDraggedItemData(props.scenarioId);

        if (
            dragData &&
            dragData.userIdentity === props.userIdentity &&
            props.itemType === dragData.itemType
        ) {
            return true;
        }

        return false;
    };

    const onDrop = (dragData: DragData) => {
        const itemDragData = dragData as CalendarPickerItemDragData;
        if (itemDragData.itemId === prevItemId.current) {
            return;
        }

        switch (itemDragData.itemType) {
            case DraggableItemTypes.CalendarNode:
                {
                    updateCalendarDisplayOrder(
                        props.userIdentity /* userIdentity */,
                        itemDragData.itemId /* draggedItemId */,
                        prevItemId.current /* prevItemId */,
                        undefined /* nextItemId */,
                        props.parentGroupId /* destParentGroupId */
                    );
                }
                break;
            case DraggableItemTypes.CalendarGroupNode:
                {
                    updateCalendarGroupDisplayOrder(
                        props.userIdentity /* userIdentity */,
                        itemDragData.itemId /* draggedItemId */,
                        prevItemId.current /* prevItemId */,
                        undefined /* nextItemId */
                    );
                }
                break;
        }
    };

    const onDragOver = () => {
        setDropViewState({
            isDragOver: true,
            draggableItemType: '',
        });
    };

    const onDragLeave = () => {
        setDropViewState({
            isDragOver: false,
            draggableItemType: '',
        });
    };

    return (
        <Droppable
            canDrop={canDrop}
            onDrop={onDrop}
            dropViewState={dropViewState}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}>
            <div
                className={classNames(styles.placeholderItem, {
                    [styles.topBorderDragOver]: dropViewState.isDragOver,
                })}
            />
        </Droppable>
    );
});
